// EDEN · tiny path-based router with a role/auth guard.
//
// 2026-09-30 redesign — two fixes for "I click and nothing much happens" (the owner's live walk):
//  1. Instant feedback. A page waits 1.5–2.7 s for the API before it can draw (measured on the
//     owner's PC). The moment a link is clicked the menu highlights, a thin progress bar runs along
//     the top, the old page fades, and — if the wait passes a quarter of a second — a skeleton of
//     the new page takes its place. Nothing ever just sits there.
//  2. No stale page. Each navigation renders into its own fresh container; if the user clicks
//     somewhere else before it finishes, the late result is thrown away instead of overwriting the
//     page they asked for second.

import { me } from "./state";
import { appRoot, autoGrow, esc } from "./dom";
import { renderChrome } from "./chrome";
import { renderMobileNav } from "./mobile-nav";
import { isNetworkError, isOfflineShowing, renderOffline } from "./offline";
import { renderSignin } from "./pages/signin";
import { renderHome } from "./pages/home";
import { renderClasses } from "./pages/classes";
import { renderClassView } from "./pages/class-view";
import { renderGradebook } from "./pages/gradebook";
import { renderDataHandling } from "./pages/data-handling";
import { renderGradebookSettings } from "./pages/gradebook-settings";
import { renderThisWeek } from "./pages/this-week";
import { renderLessonMap } from "./pages/lesson-map";
import { renderPlans } from "./pages/plans";
import { renderPlanNew } from "./pages/plan-new";
import { renderPlanView } from "./pages/plan-view";
import { renderPlanEdit } from "./pages/plan-edit";
import { renderLessonView } from "./pages/lesson-view";
import { renderTeam } from "./pages/team";
import { renderLeadProgress } from "./pages/lead-progress";
import { renderLeadTeaching } from "./pages/lead-teaching";
import { renderTeacher } from "./pages/teacher";
import { renderCalendar } from "./pages/calendar";
import { renderCalendarImport } from "./pages/calendar-import";
import { renderDashboard } from "./pages/dashboard";
import { renderAdmin } from "./pages/admin";
import { renderInsights } from "./pages/insights";
import { renderObservations } from "./pages/observations";
import { renderObservation } from "./pages/observation";
import { renderSettings } from "./pages/settings";
import { renderPlanFork } from "./pages/plan-fork";
import { renderSpeak, renderSpeakNew } from "./pages/speak";
import { renderRoadmap } from "./pages/roadmap";
import { renderBooks, renderBook } from "./pages/books";
import { renderPdfBooks, renderPdfBook } from "./pages/pdf-processor";
import { renderCoach } from "./pages/coach";
import { renderTests } from "./pages/tests";
import { renderTestEditor } from "./pages/test-editor";
import { renderTestPrint } from "./pages/test-print";
import { renderMaterialsGuide } from "./pages/materials-guide";
import { renderMaterialsStudio } from "./pages/materials-studio";
import { pageHeader, skeletonHtml } from "./ui";

type Params = Record<string, string>;
// `query` carries the URL's search params (e.g. /calendar?week=4) — pages that don't need it just
// ignore the extra arg, so adding it is backward-compatible.
type Render = (app: HTMLElement, params: Params, query: URLSearchParams) => void | Promise<void>;

interface Route {
  pattern: RegExp;
  keys: string[];
  render: Render;
  pub?: boolean;          // reachable when signed out
  role?: "lead";          // restricted to a role
  title?: string;         // the skeleton's heading while the page loads
  wide?: boolean;         // a wide workspace page (tables, two columns) — see ui.css .page-wide
}

function route(
  path: string,
  render: Render,
  opts: { pub?: boolean; role?: "lead"; title?: string; wide?: boolean } = {},
): Route {
  const keys: string[] = [];
  const pattern = new RegExp(
    "^" +
      path.replace(/:[^/]+/g, (m) => {
        keys.push(m.slice(1));
        return "([^/]+)";
      }) +
      "/?$",
  );
  return { pattern, keys, render, ...opts };
}

const routes: Route[] = [
  route("/signin", renderSignin, { pub: true }),
  route("/", renderHome, { title: "Home", wide: true }),
  route("/classes", renderClasses, { title: "Classes", wide: true }),
  route("/classes/:id", renderClassView, { title: "Class" }),
  route("/classes/:id/gradebook", renderGradebook, { title: "Class marks", wide: true }),
  route("/this-week", renderThisWeek, { title: "This week", wide: true }),
  route("/map/:node", renderLessonMap, { title: "Lesson", wide: true }),
  route("/roadmap", renderRoadmap, { title: "Year plan", wide: true }),
  route("/books", renderBooks, { title: "Books", wide: true }),
  route("/books/:id", renderBook, { title: "Book", wide: true }),
  route("/pdf-books", renderPdfBooks, { title: "PDF BOOKS", wide: true }),
  route("/pdf-books/:id", renderPdfBook, { title: "Review PDF book", wide: true }),
  route("/plans", renderPlans, { title: "My plans", wide: true }),
  route("/coach", renderCoach, { title: "Coach" }),
  route("/plan/start", renderPlanFork, { title: "Plan a lesson", wide: true }),
  route("/plan/new", renderPlanNew, { title: "Plan a lesson", wide: true }),
  route("/plan/:id", renderPlanView, { title: "Lesson plan" }),
  route("/lesson/:id/edit", renderPlanEdit, { title: "Edit plan", wide: true }), // the plan beside its materials (teacher review, 15 Oct 2026)
  // JOB-W02 · one lesson session end to end. Distinct from /lesson/:id/edit above: ":id"
  // compiles to ([^/]+), which cannot span the "/edit" segment, so the two never collide.
  route("/lesson/:id", renderLessonView, { title: "Lesson" }),
  route("/speak/new", renderSpeakNew, { title: "Talk it through" }),
  route("/speak/:id", renderSpeak, { title: "Talk it through" }),
  route("/calendar", renderCalendar, { title: "Calendar", wide: true }),
  route("/calendar/import", renderCalendarImport, { title: "Import the calendar" }),
  route("/dashboard", renderDashboard, { title: "Team board", wide: true }),
  route("/team", renderTeam, { role: "lead", title: "Team", wide: true }),
  route("/lead/progress", renderLeadProgress, { role: "lead", title: "Progress and saved activity", wide: true }),
  route("/lead/teaching", renderLeadTeaching, { role: "lead", title: "Teaching content and used materials", wide: true }),
  route("/team/:id", renderTeacher, { role: "lead", title: "Teacher", wide: true }),
  // The old Department address keeps working: it is the Team page now.
  route("/status", renderTeam, { role: "lead", title: "Team", wide: true }),
  route("/insights", renderInsights, { role: "lead", title: "Term report", wide: true }),
  route("/admin", renderAdmin, { role: "lead", title: "Setup", wide: true }),
  route("/admin/data-handling", renderDataHandling, { title: "Results handling" }),
  route("/admin/gradebook", renderGradebookSettings, { role: "lead", title: "Marks setup", wide: true }),
  route("/observations", renderObservations, { title: "Observations" }),
  route("/observation/:id", renderObservation, { title: "Observation" }),
  route("/settings", renderSettings, { title: "Settings" }),
  route("/tests", renderTests, { title: "Worksheets & tests", wide: true }),
  route("/materials-guide/:node", renderMaterialsGuide, { title: "Full teacher guide", wide: true }),
  route("/materials-studio/:node", renderMaterialsStudio, { title: "Make my worksheet", wide: true }),
  route("/tests/:id/print", renderTestPrint, { title: "Print" }),
  route("/tests/:id", renderTestEditor, { title: "Worksheet" }),
];

type NavigationGuard = { owner: HTMLElement; beforeLeave: () => boolean | Promise<boolean> };
const navigationGuards = new Set<NavigationGuard>();
/** Save first, or keep the current page with its visible failure and draft intact. */
export function registerNavigationGuard(owner:HTMLElement,beforeLeave:()=>boolean|Promise<boolean>):()=>void {
  const guard={owner,beforeLeave};navigationGuards.add(guard);
  return ()=>navigationGuards.delete(guard);
}
async function mayLeave():Promise<boolean>{
  for(const guard of navigationGuards){
    if(!guard.owner.isConnected){navigationGuards.delete(guard);continue;}
    try{if(!await guard.beforeLeave())return false;}catch{return false;}
  }
  return true;
}
let historyIndex=0;
let navigating=false;
let restoringBack: {target:number;resolve:()=>void}|null=null;
let acceptedBack:number|null=null;
export function navigate(path: string, beforeNavigate?:()=>void|Promise<void>): void {
  if(navigating)return;
  navigating=true;
  void (async()=>{
    try{
      if(!await mayLeave())return;
      await beforeNavigate?.();
      const state={...history.state,edenIndex:++historyIndex};delete state.edenReader;delete state.edenDocView;
      history.pushState(state, "", path);queueMicrotask(()=>void render());
    }
    finally{navigating=false;}
  })();
}

// ── navigation feedback ─────────────────────────────────────────────────────────────────────
let navSeq = 0; // increments per navigation; a render that finishes under an older number is stale
let skeletonTimer: number | undefined;
let lastPath = ""; // the path of the page on screen — a render of the same path is a refresh, not a move

function progressStart(): void {
  const bar = document.getElementById("eden-progress");
  if (!bar) return;
  // The bar runs just under the pilot label while the label is on screen — it never sits on the
  // owner-approved words — and at the very top once the label has scrolled away.
  const pilot = document.getElementById("eden-pilot");
  const below = pilot ? Math.max(0, Math.round(pilot.getBoundingClientRect().bottom)) : 0;
  bar.style.top = `${below}px`;
  // restart the animation even when a previous navigation's bar is still running
  bar.classList.remove("run", "done");
  void bar.offsetWidth;
  bar.classList.add("run");
}
function progressDone(): void {
  const bar = document.getElementById("eden-progress");
  if (!bar) return;
  // The new page appearing IS the "done" signal; the bar simply goes.
  bar.classList.remove("run", "done");
}

async function render(): Promise<void> {
  const url = new URL(location.href);
  const path = url.pathname;
  const user = me();
  const seq = ++navSeq;
  const root = appRoot();

  // Paint the persistent nav (desktop sidebar + phone tab bar) for this user + route. Both live
  // outside #app, so they survive the page swap below. Hidden when signed out / on /signin.
  renderChrome(path);
  renderMobileNav(path);

  // Auth guard: only /signin is public.
  for (const r of routes) {
    const m = r.pattern.exec(path);
    if (!m) continue;

    if (!r.pub && !user) return navigate("/signin");
    if (r.pub && user) return navigate("/");
    if (r.role && user?.auth_role !== r.role) return navigate("/");

    const params: Params = {};
    r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1]!)));

    // Feedback the moment the click lands: dim the old page, run the bar; after 250 ms still
    // waiting, show a skeleton with the new page's name so the user knows where they are going.
    progressStart();
    root.setAttribute("aria-busy", "true");
    root.classList.add("is-leaving");
    window.clearTimeout(skeletonTimer);
    // A re-render of the page already on screen (a picker change, a save) keeps it visible, just
    // dimmed — a skeleton there would flash away what the user is looking at.
    if (r.title && path !== lastPath) {
      skeletonTimer = window.setTimeout(() => {
        if (seq !== navSeq) return;
        root.classList.remove("is-leaving");
        root.className = r.wide ? "page-wide" : "";
        root.innerHTML = skeletonHtml(r.title!);
      }, 250);
    }

    // Each page draws into its own fresh container, swapped in only if it is still the page the
    // user wants (a slow earlier page can never overwrite a later one).
    const page = document.createElement("div");
    page.className = "page";
    try {
      await r.render(page, params, url.searchParams);
    } catch (err) {
      if (seq !== navSeq) return;
      window.clearTimeout(skeletonTimer);
      root.classList.remove("is-leaving");
      root.removeAttribute("aria-busy");
      progressDone();
      // Offline (a failed fetch): show the calm offline state instead of a blank page.
      // A real server error (ApiError) is left to bubble — that's not "offline".
      if (isNetworkError(err)) {
        root.innerHTML = "";
        renderOffline(root);
        return;
      }
      // Anything else (a plan that was deleted, a page this account may not open): say so calmly
      // and give a way on — never leave the skeleton or the old page standing as if nothing happened.
      console.error(err);
      const box = document.createElement("div");
      box.className = "page";
      box.innerHTML = `${pageHeader({ title: "This page couldn't open", sub: esc((err as Error)?.message || "Something went wrong.") })}
        <p><a class="btn secondary" data-link href="/">Go to Home</a></p>`;
      root.className = "";
      root.replaceChildren(box);
      lastPath = path;
      document.title = "EDEN";
      return;
    }
    if (seq !== navSeq) return; // the user moved on while this page loaded — drop it
    window.clearTimeout(skeletonTimer);
    // A page that navigated elsewhere itself (e.g. /plan/start → /plan/new) has already started a
    // newer render; the check above caught that. Otherwise swap the finished page in.
    root.className = r.wide ? "page-wide" : "";
    // Lead workspaces used to widen #app themselves; the flag now rides on the page container.
    if (page.classList.contains("lead-wide")) root.classList.add("page-wide");
    root.replaceChildren(page);
    root.removeAttribute("aria-busy");
    progressDone();
    // A new page starts at the top with focus on its title (a re-render of the SAME page keeps its
    // place) — BEFORE eden:mounted, so a page that scrolls to a row or focuses a field wins.
    if (lastPath !== path) {
      window.scrollTo(0, 0);
      const h1 = page.querySelector<HTMLElement>("h1");
      if (h1) {
        h1.setAttribute("tabindex", "-1");
        h1.focus({ preventScroll: true });
      }
    }
    lastPath = path;
    autoGrow(page);
    // Pages that need layout (scroll a row into view, measure, focus a field) listen for this: while
    // they were rendering they were not in the document yet.
    page.dispatchEvent(new CustomEvent("eden:mounted"));
    document.title = pageTitle(page, r.title);
    return;
  }

  // Unknown path → home (or signin if signed out).
  navigate(user ? "/" : "/signin");
}

function pageTitle(page: HTMLElement, fallback?: string): string {
  const h1 = page.querySelector("h1")?.textContent?.trim();
  const name = h1 || fallback;
  return name ? `${name} · EDEN` : "EDEN";
}

export function initRouter(): void {
  historyIndex=Number.isInteger(history.state?.edenIndex)?history.state.edenIndex:0;
  history.replaceState({...history.state,edenIndex:historyIndex},'',location.href);
  window.addEventListener("popstate", () => {
    const target=Number.isInteger(history.state?.edenIndex)?history.state.edenIndex:historyIndex;
    if(restoringBack){const restore=restoringBack;restoringBack=null;restore.resolve();return;}
    if(acceptedBack===target){acceptedBack=null;historyIndex=target;void render();return;}
    // Reader overlays share their page's route index. Forward to an old marker
    // and Back from it must both leave the underlying editable page intact.
    if(target===historyIndex)return;
    // Restore the current URL before a save: a save may update this page's query string.
    const previous=historyIndex;
    navigating=true;
    void (async()=>{
      await new Promise<void>(resolve=>{restoringBack={target,resolve};history.go(previous-target);});
      try{if(await mayLeave()){acceptedBack=target;history.go(target-previous);}}
      finally{navigating=false;}
    })();
  });
  // Self-heal: when the signal returns (or the offline panel's "Try again" fires), re-render
  // the current route so the data loads without a manual reload. ONLY when the offline panel
  // is what's showing — a working page (e.g. a half-typed plan form) must never be wiped by a
  // connectivity blip; its own autosave handles the reconnect.
  window.addEventListener("online", () => {
    if (isOfflineShowing()) void render();
  });
  window.addEventListener("eden:rerender", () => void render());
  // Intercept in-app links marked with data-link.
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as HTMLElement).closest("a[data-link]") as HTMLAnchorElement | null;
    if (a && a.origin === location.origin) {
      e.preventDefault();
      navigate(a.pathname + a.search + a.hash); // keep the query string (e.g. /calendar?week=4) and #section on in-app links
    }
  });
  void render();
}
